import { useCallback, useMemo } from 'react';
import { LocationControllerApi } from '../api/generated';
import type { UserDetailResponse } from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';
import {
  LocationAssignmentError,
  LocationCreatedWithFailedRightsError,
  applyLocationAssignmentUpdates,
  buildLocationAssignmentUpdates,
  getLocationUserRoles,
  hasAnyRole
} from '../services/locationUserService';
import type { LocationUserRoles } from '../services/locationUserService';
import { getLocationColorHex, resolveLocationColorId } from '../services/locationColorService';
import { getUserDisplayName } from '../services/userLabelService';
import type { LocationColorId } from '../constants/locationColors';
import { useLocations } from './useLocations';
import { useUsers } from './useUsers';

// Falls back to the raw id so a user who has since been deleted is still named
// in the failure message rather than dropping out of the count.
const resolveUserName = (users: UserDetailResponse[], userId: string): string => {
  const user = users.find((u) => u.userId === userId);
  return user ? getUserDisplayName(user) : userId;
};

export interface LocationSettingsValues {
  name: string;
  colorId: LocationColorId;
  // One row per user with the roles they hold here, mirroring the per-location
  // roles table on the Users page. Anything derived from this (the assigned
  // count, for one) is computed where it is displayed rather than stored
  // alongside, so the two cannot drift apart.
  userRoles: LocationUserRoles[];
}

// A persisted row is the editable values plus what only the server side knows.
// Declared by extension so a field added to the form cannot silently go missing
// from the row, which two near-identical interfaces would have allowed.
export interface LocationSettingsRow extends LocationSettingsValues {
  id: string;
}

export interface UseLocationsSettingsResult {
  rows: LocationSettingsRow[];
  users: UserDetailResponse[];
  isLoading: boolean;
  // Exposed on its own because the edit modal can open while users are still
  // loading (locations resolve independently): its rights grid must show a
  // loading note rather than an empty grid that reads as "no rights held".
  isUsersLoading: boolean;
  // Kept apart because they mean different things to the user: locations failing
  // to load leaves nothing to show, whereas users failing to load leaves a
  // usable table whose rights grids must not be presented as "no users exist".
  locationsLoadError: string | null;
  usersLoadError: string | null;
  // Rejects with LocationAssignmentError when the location itself was saved but
  // some of its role writes were not.
  saveLocation: (locationId: string, values: LocationSettingsValues) => Promise<void>;
  // Rejects with LocationCreatedWithFailedRightsError when the location was
  // created but some of its role writes were not.
  //
  // `copyFromLocationId` is create-only and deliberately not part of
  // LocationSettingsValues: it describes how the location is seeded, not anything
  // the location goes on to hold, so a persisted row has no such value to carry.
  // Null means create an ordinary empty location.
  addLocation: (values: LocationSettingsValues, copyFromLocationId: string | null) => Promise<void>;
  deleteLocation: (locationId: string) => Promise<void>;
}

/**
 * Backs the global locations settings page.
 *
 * Two sources are stitched together, both real:
 *  - the location itself (name, colour) comes from the location endpoints, and
 *    every edit is a write the backend keeps. Colour is stored as a hex string
 *    and converted to a palette token on the way in and back on the way out
 *    (see locationColorService), so this page and the rest of the UI keep
 *    working in token ids.
 *  - user assignment comes from the real per-user `roleAssignments` and is
 *    saved with the existing `updateUser`, so it stays in sync with the Users
 *    page in both directions.
 *
 * There is no local edit state: after a write the location list is re-read, so
 * what the table shows is what the backend holds.
 */
export function useLocationsSettings(): UseLocationsSettingsResult {
  const {
    locations,
    isLoading: isLoadingLocations,
    loadError: locationsError,
    refresh: refreshLocations
  } = useLocations();
  const {
    users,
    isLoading: isLoadingUsers,
    loadError: usersError,
    updateUser,
    fetchUsers
  } = useUsers();
  const locationApi = useMemo(() => new LocationControllerApi(createApiConfiguration()), []);

  const rows = useMemo<LocationSettingsRow[]>(
    () =>
      locations.map((location) => ({
        id: location.id,
        name: location.name,
        colorId: resolveLocationColorId(location),
        userRoles: getLocationUserRoles(users, location.id)
      })),
    [locations, users]
  );

  /**
   * Saves an existing location: its user roles first, then its own fields.
   *
   * That order is deliberate. The role writes are the half that can fail
   * per-user, and committing the rename and recolour only once they have
   * settled keeps a partial failure from leaving the location renamed while the
   * operator is told the save did not go through. Create cannot work this way —
   * the location has to exist before an assignment can point at it — so the two
   * are asymmetric on purpose.
   */
  const saveLocation = useCallback(
    async (locationId: string, values: LocationSettingsValues) => {
      const updates = buildLocationAssignmentUpdates(users, locationId, values.userRoles);

      const { failedUserIds, savedCount, firstFailureCause } =
        await applyLocationAssignmentUpdates(updates, updateUser);

      if (failedUserIds.length > 0) {
        throw new LocationAssignmentError(
          failedUserIds.map((userId) => resolveUserName(users, userId)),
          savedCount,
          firstFailureCause
        );
      }

      await locationApi.updateLocation({
        locationId,
        updateLocationRequest: {
          name: values.name.trim(),
          color: getLocationColorHex(values.colorId)
        }
      });

      await refreshLocations();
    },
    [locationApi, refreshLocations, users, updateUser]
  );

  /**
   * Creates a location, then assigns its user roles. Two calls, because the
   * backend has no single endpoint that does both.
   *
   * The two halves fail independently and are reported as such: once the POST
   * has returned, the location exists, so a failure in the role writes must
   * not be presented as "could not create the location". That case throws
   * LocationCreatedWithFailedRightsError, carrying the new id so the caller can
   * switch the open form to editing it rather than POSTing again.
   */
  const addLocation = useCallback(
    async (values: LocationSettingsValues, copyFromLocationId: string | null): Promise<void> => {
      const details = {
        name: values.name.trim(),
        color: getLocationColorHex(values.colorId)
      };

      // Two endpoints, because a copy is a different operation on a different
      // resource: it POSTs to the location being copied. Both grant the calling
      // admin read+write, so everything below is identical either way.
      const created = copyFromLocationId
        ? (
            await locationApi.copyLocation({
              locationId: copyFromLocationId,
              copyLocationRequest: details
            })
          ).data.location
        : (await locationApi.createLocation({ createLocationRequest: details })).data;

      // Without an id there is nothing to attach roles to, and an undefined one
      // would be written into real user records as a role assignment for a
      // location that does not exist.
      if (!created?.id) {
        throw new Error('The create location response carried no id');
      }

      try {
        // The POST assigns the calling administrator the planner role on the new
        // location as a side effect. The users list predates that assignment, so
        // diffing against it would see them as holding no role, read the untouched
        // grid as "still none", and send nothing — or, worse, revoke the
        // assignment the moment anything else re-seeded the row. Refetching first
        // makes the diff compare against what the backend actually holds.
        // Everything past this point runs with the location already created, so no
        // failure here may be reported as a failed create. Each one is converted
        // into the "created, but roles are outstanding" case, which is the
        // operator's actual situation and the only one with a recovery path.
        let freshUsers: UserDetailResponse[];
        try {
          freshUsers = await fetchUsers();
        } catch (cause) {
          // Refusing to diff against a stale list is the whole point: those
          // payloads carry every OTHER location's roles through, so a stale one
          // can revoke access the user still holds elsewhere.
          const requested = values.userRoles.filter(hasAnyRole);
          throw new LocationCreatedWithFailedRightsError(
            created.id,
            requested.map((assignment) => resolveUserName(users, assignment.userId)),
            0,
            cause
          );
        }

        const updates = buildLocationAssignmentUpdates(freshUsers, created.id, values.userRoles);
        const { failedUserIds, savedCount, firstFailureCause } =
          await applyLocationAssignmentUpdates(updates, updateUser);

        if (failedUserIds.length === 0) return;

        throw new LocationCreatedWithFailedRightsError(
          created.id,
          failedUserIds.map((userId) => resolveUserName(freshUsers, userId)),
          savedCount,
          firstFailureCause
        );
      } finally {
        // The location exists from the moment the POST returns, so the table
        // shows it either way — including when the roles half failed and the
        // operator is about to retry those against the row that is now there.
        await refreshLocations();
      }
    },
    [locationApi, refreshLocations, fetchUsers, updateUser, users]
  );

  // The backend archives rather than hard-deletes (its ADR 0005): the location
  // leaves the listing and releases its name, while scenarios, patients and
  // bookings that reference it are kept. From here that is simply a delete —
  // the re-read no longer returns it.
  const deleteLocation = useCallback(
    async (locationId: string): Promise<void> => {
      await locationApi.deleteLocation({ locationId });
      await refreshLocations();
    },
    [locationApi, refreshLocations]
  );

  return {
    rows,
    users,
    isLoading: isLoadingLocations || isLoadingUsers,
    isUsersLoading: isLoadingUsers,
    locationsLoadError: locationsError,
    usersLoadError: usersError,
    saveLocation,
    addLocation,
    deleteLocation
  };
}
